import React, { useEffect, useState } from "react";
import { Form, Input, Button, message, Card, Spin, Alert } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import guideVideoApi from "../../../api/guideVideoApi";

// Cấu hình URL video hướng dẫn "mua sắm hoàn tiền" cho từng sàn.
// App + web đọc file tĩnh /img/guide-videos.json; sửa ở đây → ghi lại file đó.
const PLATFORMS = [
    { key: "shopee", label: "Shopee", color: "#EE4D2D" },
    { key: "lazada", label: "Lazada", color: "#0F146D" },
    { key: "tiktok", label: "TikTok", color: "#111827" },
    // ShopeeFood chưa có video hướng dẫn — để trống thì app/web hiện ảnh mascot,
    // dán URL vào là màn "Hoàn tiền ShopeeFood" hiện video ngay (không cần build).
    { key: "shopeefood", label: "ShopeeFood", color: "#FF7A00" },
];

const EMPTY = Object.fromEntries(PLATFORMS.map((p) => [p.key, ""]));

function GuideVideoConfig() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [urls, setUrls] = useState(EMPTY);

    useEffect(() => {
        (async () => {
            try {
                const res = await guideVideoApi.getConfig();
                const videos = res.data?.data?.videos || {};
                const init = Object.fromEntries(
                    PLATFORMS.map((p) => [p.key, videos[p.key]?.url || ""])
                );
                form.setFieldsValue(init);
                setUrls(init);
            } catch {
                message.error("Không tải được cấu hình video");
            } finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onFinish = async (values) => {
        setSaving(true);
        try {
            const videos = {};
            PLATFORMS.forEach((p) => {
                videos[p.key] = { url: (values[p.key] || "").trim(), poster: "" };
            });
            await guideVideoApi.updateConfig(videos);
            message.success("Đã lưu cấu hình video hướng dẫn");
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu thất bại");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center">
                <Spin />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl">
            <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faVideo} className="text-rose-600" />
                <h1 className="text-xl font-bold m-0">Video hướng dẫn (Mua sắm hoàn tiền)</h1>
            </div>
            <p className="text-sm text-content-3 mb-4">
                Video autoplay ở vùng đầu màn hoàn tiền (app + web). Đổi URL là app/web tự
                nhận, không cần build lại. Để trống = dùng lại ảnh mascot mặc định.
            </p>
            <Alert
                type="warning"
                showIcon
                className="mb-5"
                message="Chỉ dùng video .mp4 (H.264)"
                description="File .mov/HEVC từ iPhone KHÔNG phát được trên Chrome/Firefox và nhiều máy Android. Hãy convert sang .mp4 H.264 trước khi upload R2."
            />
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={(_, all) => setUrls(all)}
            >
                {PLATFORMS.map((p) => (
                    <Card
                        key={p.key}
                        size="small"
                        className="mb-4"
                        title={
                            <span style={{ color: p.color }} className="font-semibold">
                                {p.label}
                            </span>
                        }
                    >
                        <div className="flex gap-4 items-start">
                            <div className="flex-1">
                                <Form.Item
                                    name={p.key}
                                    label="URL video (.mp4)"
                                    rules={[{ type: "url", message: "URL không hợp lệ" }]}
                                    className="mb-0"
                                >
                                    <Input
                                        placeholder="https://.../video.mp4 (để trống = ảnh mascot)"
                                        allowClear
                                    />
                                </Form.Item>
                            </div>
                            <div className="w-28 shrink-0">
                                {urls[p.key] ? (
                                    <video
                                        src={urls[p.key]}
                                        className="w-28 rounded-md border bg-surface-sunken"
                                        style={{ aspectRatio: "3/4", objectFit: "cover" }}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                    />
                                ) : (
                                    <div
                                        className="w-28 rounded-md border bg-surface-sunken flex items-center justify-center text-content-3"
                                        style={{ aspectRatio: "3/4" }}
                                    >
                                        <FontAwesomeIcon icon={faVideo} size="lg" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />}
                >
                    Lưu cấu hình
                </Button>
            </Form>
        </div>
    );
}

export default GuideVideoConfig;
